/**
 * Touch-device helpers.
 *
 * Used to gate mobile/tablet-only workarounds so desktop behaviour stays exactly as it
 * was. The main case: on Android/iOS, opening the soft keyboard shrinks the viewport,
 * which fires `resize` / ResizeObserver. If that triggers a DataTables `draw()`, every
 * cell's HTML is regenerated, the focused input is replaced by a new node and the
 * keyboard is dismissed the moment the user taps the field.
 */

/**
 * True only when the primary pointer is touch (phones, tablets, and Chrome/Safari
 * device emulation). A desktop with a touchscreen still reports `pointer: fine`
 * because the mouse is the primary pointer, so it is treated as desktop.
 */
export function isTouchDevice() {
  if (typeof window === "undefined") return false;
  try {
    if (typeof window.matchMedia === "function") {
      if (window.matchMedia("(pointer: coarse)").matches) return true;
      // Primary pointer is a mouse/trackpad -> desktop, regardless of touch support.
      if (window.matchMedia("(pointer: fine)").matches) return false;
    }
  } catch (e) {
    // matchMedia unsupported - fall through to the touch-capability check below.
  }
  const maxTouchPoints =
    (typeof navigator !== "undefined" && navigator.maxTouchPoints) || 0;
  return maxTouchPoints > 0 || "ontouchstart" in window;
}

/**
 * Input types that take typed text (and therefore raise the soft keyboard).
 * Checkboxes/radios/buttons are excluded: they are focusable but have no keyboard,
 * so a redraw while one of them is focused is harmless.
 */
const NON_TEXT_INPUT_TYPES = new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "file",
  "image",
  "range",
  "color",
]);

/**
 * True when the element currently holding focus is a text-entry control inside
 * `container` (i.e. the user is typing in a cell and must not lose that input).
 */
export function isEditableElementFocusedWithin(container) {
  if (!container || typeof document === "undefined") return false;
  const active = document.activeElement;
  if (!active || active === document.body) return false;
  if (typeof container.contains !== "function" || !container.contains(active)) {
    return false;
  }
  if (active.isContentEditable === true) return true;
  const tag = (active.tagName || "").toUpperCase();
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag !== "INPUT") return false;
  const type = (active.getAttribute("type") || "text").toLowerCase();
  return !NON_TEXT_INPUT_TYPES.has(type);
}
